// @flow

import $ from "../vendor/jquery";
import Settings from "./Settings";
import {calcAngle, limit_timestamp} from "../util";
import global from "./Global";
import type {ConvertedMeasurementType, IntegerType, MeasurementType} from "../model";
import Legacy from "../Legacy";
import ProductTransformer from "../ProductTransformer";
import SettingsStore from "../stores/SettingsStore";

/**
 * Tekitab korrigeeritud andmed drow objektile.
 * Juurde tekivad elemendid:
 * drow["corr_required"] - drow["required"] korrigeeritud väärtused
 * drow["corr_series"] - drow["series"] korrigeeritud väärtused
 * drow["corr_shifts"] - iga rea korrigeerimiseks kasutatud diff väärtused
 */
function correct_grid_row(drow: ConvertedMeasurementType) {
    let settings = Settings.getInstance();

    let LINE_START_X = 0;
    //var LINE_POINTS = settings.getMeasurementPoints();
    //    'linepoints': [1,5,...,2], // len 9, nominaalpunktide kaugus klaasi servast, each num is INT
    //  'required': [[VAL1,TOLERANCE1Y,TOLERANCE1A,TOLERANCE2Y,TOLERANCE2A],...,[VAL9,...]] // len 9, each num is INT
    // series - massiiv joontest, iga joon 1..3 mõõtmist
    let series = drow.series;
    let c_series = [];
    let s_series = [];
    let c_required = [];
    for (let l = 0; l < series.length; l++) {
        let line = series[l];
        if (!line) continue;
        let c_line = [];
        let s_line = [];
        // looping over lines
        for (let m = 0; m < line.length; m++) {
            let single = line[m];
            if (!single) continue;
            let LINE_POINTS = single.length;
            // looping over measurements in a singe test of a line
            // first pick the linepoint distance from left and requested value
            let lx, ly;
            try {
                lx = drow.linepoints[l * 3 + 1];
                ly = drow.required[l * 3 + 1][0];
            } catch (e) {
                continue;
            }
            // loop over measure points finding the central required point
            let d = LINE_START_X;
            let line_end = drow.lineends[l];
            lx = line_end - lx; // measure point distance is from right
            let line_step = (line_end - LINE_START_X) / LINE_POINTS;
            let x1 = -1; let y1 = -1000; // point to the left
            let x2 = -1; let y2 = -1000; // point to the right
            //Legacy.debug("single.length: "+single.length);
            for (let x = 0; x < single.length; x++) {
                if (d > lx) {
                    x1 = d - line_step;
                    y1 = single[x - 1];
                    x2 = d;
                    y2 = single[x];
                    break; // fire just once
                }
                d += line_step;
            }
            // calculate linear interpolant of two points
            // eslint-disable-next-line eqeqeq
            let ymedium;
            if (x2 != x1) ymedium = y1 + (y2 - y1) * ((lx - x1) / (x2 - x1));
            else ymedium = (y1 + y2) / 2; // non-interpolation medium
            //ymedium=(y1+y2)/2; // non-interpolation medium
            let ydiff = ly - ymedium; // simplest ydiff
            // correct nominals
            let n0 = drow.required[l * 3];
            let n1 = drow.required[l * 3 + 1];
            let n2 = drow.required[l * 3 + 2];
            let c0 = n0[0] / n1[0];
            let c2 = n2[0] / n1[0];
            let k0, k2;
            // eslint-disable-next-line eqeqeq
            if (ymedium != 0) {
                k0 = (ymedium - n1[0]) * c0;
                k2 = (ymedium - n1[0]) * c2;
            } else {
                k0 = 0;
                k2 = 0;
            }
            let cn0, cn1, cn2;
            if (settings.isToleranceCompensation()) {
                cn0 = [(n0[0] == null ? null : n0[0] + k0), n0[1], n0[2], n0[3], n0[4]];
                cn1 = n1;
                cn2 = [(n2[0] == null ? null : n2[0] + k2), n2[1], n2[2], n2[3], n2[4]];
            } else {
                cn0 = n0;
                cn1 = n1;
                cn2 = n2;
            }
            c_required.push(cn0);
            c_required.push(cn1);
            c_required.push(cn2);
            // correct the line
            ydiff = 0; // no correction of actual measurements
            let c_single = [];
            for (let x = 0; x < single.length; x++) {
                c_single.push(single[x] + ydiff);
            }
            c_line.push(c_single);
            s_line.push(ydiff);
        }
        c_series.push(c_line);
        s_series.push(s_line);
    }
    // Copy additional points:
    if (drow.required.length > c_required.length) c_required = c_required.concat(drow.required.slice(c_required.length));
    // store
    drow.corr_required = c_required;
    drow.corr_series = c_series;
    drow.corr_shifts = s_series;
}

/**
 * Returns array:
 * [
 * 		[diff, status, y, x],
 * 		...
 * ]
 *
 * Iga nominaali kohta üks element, mis kajastab:
 * 		diff - nominaalpunktis mõõdetud väärtuse ja ettenähtud väärtuse vahe
 * 		status - üks järgmistest:
 * 		    1 - ok (diff < alumine viga),
 * 		    2 - warning (diff < ülemine viga),
 * 		    3 - error (diff > ülemine viga)
 * 		y - nominaalpunktis mõõdetud väärtus
 * 	    x - nominaali x koordinaat
 *
 */
function analyze_grid_row(drow: ConvertedMeasurementType): Array<[IntegerType, IntegerType, IntegerType, IntegerType]> {
    let LINE_START_X = 0;
    //var LINE_POINTS = settings.getMeasurementPoints();

    let req = drow.corr_required;
    //req=drow["required"];
    //Legacy.debug("drow[required]: "+drow["required"]);
    if (!req) return false;
    let t = [];
    for (let i = 0; i < req.length ; i++) {
        let rp: Array<[IntegerType, IntegerType, IntegerType, IntegerType, IntegerType]> = req[i]; // requirement point with value and 4 tolerances
        let ly = rp[0]; // required value
        let lx = drow.linepoints[i]; // x coord of requirement point
        //Legacy.debug("i, lx: "+i+" "+lx);
        // get corresponding line and measurement series
        //'series': [ [[0,...,99],..,[0,...,99]], ... ,[[0,...,99]] ]
        // lnum = Math.floor(i / 3);
        let lnum = drow.lineinfo[i][0];
        let line = drow.corr_series[lnum];
        let single = (line != null ? line[line.length - 1] : null);
        let LINE_POINTS = 0;
        if (single) LINE_POINTS = single.length;
        // find corresponding point
        let d = LINE_START_X;
        let line_end = drow.lineends[lnum];
        lx = line_end - lx; // measure points are from right end
        let line_step = (line_end - LINE_START_X) / LINE_POINTS;
        let x1 = -1;
        let y1 = -1000; // point to the left
        let x2 = -1;
        let y2 = -1000; // point to the right
        if (single) {
            for (let x = 0; x < single.length; x++) {
                if (d > lx) {
                    x1 = d - line_step;
                    y1 = single[x - 1];
                    x2 = d;
                    y2 = single[x];
                    break; // fire just once
                }
                d += line_step;
            }
        }
        // calculate linear interpolant of two points
        //Legacy.debug("y2, y1: "+y2+" "+y1);
        //Legacy.debug("lx, x1: "+lx+" "+x1);
        //Legacy.debug("x2, x1: "+x2+" "+x1);
        let ymedium;
        if (x2 != x1) ymedium = y1 + (y2 - y1) * ((lx - x1) / (x2 - x1));
        else ymedium = (y1 + y2) / 2; // non-interpolation medium
        // diff is difference between required and real value
        //Legacy.debug("ly: "+ly);
        //Legacy.debug("ymedium: "+ymedium);
        let diff = Math.round(Math.abs(ly - ymedium));
        let s = getNominalStatus(rp, diff);
        let t1 = [diff, s, ymedium, (x2 != x1 ? (x2 + x1) / 2 : x1)];  // error, status, averaged measurement, x
        //Legacy.debug("t1: "+t1);
        t.push(t1); // create a list of value,status pairs: status 1 ok, 2, warn, 3 wrong
    }
    return t;
}

/**
 *
 * @param {ConvertedMeasurementType} drow
 */
function analyze_grid_row_angles(drow: ConvertedMeasurementType): ?Array<[IntegerType, IntegerType, number, null]> {
    if (drow.angles == null || drow.analyzed == null) return null;

    let t = [];
    for (let i = 0; i < drow.angles.length; i++) {
        /*
         * [v, t1p, t1m, t2p, t2m, point1, point2], kus v - nominaalväärtus, t1p - ülemine 1. tolerants, t1m - alumine 1. tolerants, t2p - ülemine 2. tolerants, t2m - alumine 2. tolerants
         */
        let a = drow.angles[i];
        // drow.lineinfo = [lineNo, pointName, isAdas]
        let pi = [];
        for (let j = 0; j < drow.lineinfo.length; j++) {
            if (drow.lineinfo[j][1] === a[5] || drow.lineinfo[j][1] === a[6]) {
                pi.push(drow.analyzed[j]);
            }
        }
        if (pi.length != 2) continue;

        // [diff, status, y, x]
        let angle = calcAngle(pi[0][3], pi[0][2], pi[1][3], pi[1][2]) * 10;
        let diff = Math.round(Math.abs(a[0] - angle));
        let s = getNominalStatus(a, diff);
        t.push([
            diff, s, angle, null
        ]);
    }
    return t;
}

/**
 *
 * @param nominal
 * @param diff Difference between actual value and nominal value (absolute value!)
 * @returns {number} Status: 1 - ok, 2 - warning, 3 - wrong
 */
function getNominalStatus(nominal: Array<[IntegerType, IntegerType, IntegerType, IntegerType, IntegerType]>, diff: IntegerType) : IntegerType {
    if (diff >= nominal[4] || diff >= nominal[3]) return 3; // really wrong
    else if (diff >= nominal[2] || diff >= nominal[1]) return 2; // warning
    return 1;   // ok
}

/**
 * Rakendab sisendmassiivi kõigile ridadele convert_data_row() meetodit
 * @param indata
 * @returns {ConvertedMeasurementType[]}
 */
function convert_data_format(indata) {
    let settings = Settings.getInstance();

    let res = new Array(settings.getWagonCount() + 1);
    for (let i = 0; i <= settings.getWagonCount(); i++) {
        res[i] = null;
    }
    for (let i = 0; i < indata.length; i++) {
        let orow = indata[i];
        let nrow = convert_data_row(orow);
        if (nrow && (nrow.wagon || nrow.wagon === 0)) {
            //Legacy.debug("wagon found "+nrow["wagon"]);
            if (nrow.wagon >= 0 && nrow.wagon <= settings.getWagonCount()) {
                res[nrow.wagon] = nrow;
                //Legacy.debug("wagon stored "+nrow["wagon"]);
            }
        }
        //res.push(nrow);
    }
    return res;
}

/**
 * Teisendab JSON struktuurina antud mõõteandmed sisemiseks stuktuuriks järgmisel kujul:
 * <pre>
 * nrow = {
 *   "wagon" : vaguni_number,	// 0 -> manual, 1 ... n
 *   "skeleton" : vaguni_raam,
 *   "codeVariant" : toote_variant,
 *   "description1" : kirjeldus1,
 *   "description2" : kirjeldus2,
 *   "timestamp" : algus_aeg,
 *   "end_timestamp": lopp_aeg,
 *   // Joone pikkused iga joone kohta, kokku 5 tk
 *   "lineends" : [joone_pikkus[0], ..., joone_pikkus[4]],
 *   // Nominaalide kaugused otspunktidest: 5 joont x 3 nominaali = kokku 15 nominaali kaugust + täiendavate punktide omad
 *   "linepoints" : [nominaali_kaugus[0], ...],     // [1A, 1B, 1C, 2A, 2B, 2C, 3A, 3B, 3C, 2P50, 2P100, ...]
 *   // Nominaalid
 *   "required" : [
 *   	// Iga joone kohta 3 nominaali, kokku 15 tk + täiendavate punktide omad
 *   	// Iga nominaali korral: väärtus, ülemine tolerants 1, alumine tolerants 1, ülemine tolerants 2, alumine tolerants 2
 *   	[nominaal[0]["v"], nominaal[0]["t1p"], nominaal[0]["t1m"], nominaal[0]["t2p"], nominaal[0]["t2m"]],
 *   	...
 *   ],
 *   "angles: [
 *      // Nurga info
 *   	[nominaal[0]["v"], nominaal[0]["t1p"], nominaal[0]["t1m"], nominaal[0]["t2p"], nominaal[0]["t2m"]],
 *   ],
 *   // Jooned
 *   "series" : [
 *   	// Jooni kokku 5
 *   	[
 *   		// Max 3 mõõtmist, iga mõõtmine 100 punkti
 *   		[mootejoon_1_1[0], ..., mootejoon_1_1[99]],
 *   		...
 *   	],
 *   	...
 *   ],
 *   // Toormõõtmised
 *   "rawdata" : [
 *   	// Toormõõtmisi kokku 5 (1 iga joone kohta), iga mõõtmine 500 punkti
 *   	[mootejoon1_toor[0], ..., mootejoon1_toor[499]],
 *   	...
 *   ],
 *   // Veateated
 *   "error" : [ viga[0], ... ],
 *
 *   // Korrigeeritud jooned. Esineb juhul, kui on lastud läbi funktsiooni correct_grid_row()
 *   "corr_series" : [...],
 *   "corr_required": [...],
 *   "corr_shifts": [...],
 *   // Analüüsitud andmed. Funktsiooni analyze_grid_row() välund - iga nominaalpunkti kohta
 *   "analyzed": [[diff, status, y, x], ...]
 *   // Nurkade analüüsitud andmed
 *   "analyzed_angles": [[diff, status, y], ...]
 *   // Joonte punktide info (st - mis punktid on linepoints, required ja analyzed massiivides)
 *   lineinfo: [[lineNo, pointName, isAdas], ...]   // lineNo = 0, ..., 5; pointName = "Ai", "Bi", "Ci", "P50", "P100", ...
 * }
 * </pre>
 * @param orow
 * @returns {ConvertedMeasurementType}
 */
function convert_data_row(orow: MeasurementType): ConvertedMeasurementType {
    if (!orow) return {};

    let settings = Settings.getInstance();
    let productTransformer = new ProductTransformer(SettingsStore.getProductConfig());
    let nrow: ConvertedMeasurementType = {};

    if (orow.vaguni_number || orow.vaguni_number === 0) nrow.wagon = orow.vaguni_number;
    else nrow.wagon = "?";
    if (orow.vaguni_number == -1) nrow.wagon = 0;
    if (orow.vaguni_raam) nrow.skeleton = orow.vaguni_raam;
    else nrow.skeleton = "?";
    if (orow.toote_variant || orow.toote_variant === "") nrow.codeVariant = orow.toote_variant;
    else nrow.codeVariant = "?";
    if (orow.kirjeldus1) nrow.description1 = orow.kirjeldus1;
    else nrow.description1 = "?";
    if (orow.kirjeldus2) nrow.description2 = orow.kirjeldus2;
    else nrow.description2 = "?";
    if (orow.algus_aeg) nrow.timestamp = limit_timestamp(orow.algus_aeg);
    else nrow.timestamp = "?";
    if (orow.lopp_aeg) nrow.end_timestamp = limit_timestamp(orow.lopp_aeg);
    else nrow.end_timestamp = "?";

    if (orow.ahju_ring) nrow.furnaceRing = orow.ahju_ring;
    else nrow.furnaceRing = "?";
    if (orow.klaasi_id) nrow.glassId = orow.klaasi_id;
    else nrow.glassId = "?";

    nrow.lineinfo = [];
    nrow.lineends = [];
    nrow.linepoints = [];
    nrow.required = [];
    nrow.angles = [];

    if (orow.tooteinfo) {
        for (let i = 0; i < orow.tooteinfo.length; i++) {
            nrow.lineends.push(orow.tooteinfo[i].lineLength);
        }
        let nominals = productTransformer.getOrderedNominals(orow.tooteinfo);

        for (let n of nominals) {
            switch (n.name == null ? "" : n.name.substring(0, 1)) {
                case "A":   // ANGLE:
                    nrow.angles.push([
                        n.value,
                        n.upperTolerance1,
                        n.lowerTolerance1,
                        n.upperTolerance2,
                        n.lowerTolerance2,
                        n.point1,
                        n.point2
                    ]);
                    break;
                case "P":   // Pi
                case "H":   // Hi
                default:
                    nrow.lineinfo.push([n.line, n.name, n.isAdas]);
                    nrow.linepoints.push(n.distance);
                    nrow.required.push([
                        n.value,
                        n.upperTolerance1,
                        n.lowerTolerance1,
                        n.upperTolerance2,
                        n.lowerTolerance2
                    ]);
                    break;
            }
        }
    }

    let series = [];

    // TODO: noData kasutamine on väga küsitav - potentsiaalne probleemide koht...
    let isEmpty = true;
    let lineCount = settings.getMeasurementLineCount();
    if (orow.joonte_arv) lineCount = 0 + orow.joonte_arv;

    if (orow.mootejoon) {
        for (let i = 0; i < lineCount; i++) {
            if (orow.mootejoon[i] && orow.mootejoon[i].length > 0) {
                series.push(orow.mootejoon[i]);
                isEmpty = false;
            } else {
                series.push(null);
                // series.push(noData);   //AA: Tsükli asemel valmis data 6x
            }
        }
    }
    if (isEmpty) series = []; // AA clear empty lines
    nrow.series = series;

    series = [];
    if (orow.mootejoon_toor) {
        for (let i = 0; i < lineCount; i++) {
            if (orow.mootejoon_toor[i] && orow.mootejoon_toor[i].length > 0) series.push(orow.mootejoon_toor[i]);
            else
                series.push(null);
            //series.push(noData);
        }
    }
    nrow.rawdata = series;

    if (orow.viga && $.isArray(orow.viga) && orow.viga.length > 0) {
        //Legacy.debug("len "+orow["viga"].length);
        //nrow["description2"]="<b>Viga!</b> "+nrow["description2"];
        nrow.error = orow.viga;
    }

    // Teeme kohe ära analüüsi:
    analyze_drow(nrow);
    return nrow;
}

function analyze_drow(drow: ConvertedMeasurementType): void {
    if (!drow.corr_series || global.force_correct) correct_grid_row(drow);
    if (!drow.analyzed) {
        let tmp = false;
        try {
            tmp = analyze_grid_row(drow);
        } catch (err) {
            Legacy.debug("catched :" + err);
        }
        if (tmp) {
            drow.analyzed = tmp;
        }
        drow.analyzed_angles = analyze_grid_row_angles(drow);
    }
}

export {correct_grid_row, convert_data_format, convert_data_row};
